import { ProtectedRoute, SuspenseRoute } from "./Components/ProtectedRoute";
import { FullPageSpinner } from "./Components/Spinner";
import { Mixpanel } from "./utils/mixpanelWrapper";
import { Router, navigate, Redirect } from "@reach/router";
import { useCompanyInfo, SetCompany } from "./redux/company";
import { useExperimentFlag } from "./utils/experiment-context";
import { useSelector, useDispatch } from "react-redux";
import * as R from "ramda";
import * as UserRedux from "./redux/user";
import { MigrationPage, migrationStub } from "./MigrationPage";
import CustomTableau from "./CustomTableau";
import CampaignMappingTool from "./CampaignMappingTool";
import DataFreshness from "./DataFreshness";
import ExpectedBookings from "./ExpectedBookings";
import HomePage from "./HomePage";
import Insights from "./AMC/Insights";
import LinearDelivery from "./LinearDelivery";
import LinearPerformance from "./Performance/LinearPerformance";
import MetaBuying from "./MetaBuying";
import MetaLiftStudy from "./MetaLiftStudy";
import HROAS from "./HROAS";
import NotFound from "./NotFound";
import Properties from "./Properties";
import React, { useMemo, useEffect } from "react";
import Slides from "./Slides";
import StreamingDelivery from "./StreamingDelivery";
import StreamingGeoTargeting from "./StreamingGeoTargeting";
import StreamingPerformance from "./Performance/StreamingPerformance";
import StreamingWizard from "./StreamingWizard";
import TVADCrossChannel from "./TVADCrossChannel";
import useLocation from "./utils/hooks/useLocation";
import WebTrafficOptimization from "./WebTrafficOptimization";

const CrossChannel = React.lazy(() => import("./CrossChannel"));

// The gateway hands only these single-channel subpaths to tinuiti-frontend (the company-scoped rules
// in k8s/bpm-frontend/values.prod-eks_infra_us-west-2.yaml). Any other subpath comes straight back
// here after MigrationPage reloads, so render NotFound for it instead of reloading.
const singleChannelStub = (...otherPages) => {
  const SingleChannel = migrationStub("Single Channel");
  const pages = [
    "(overview|delivery|metrics-table|_)[^/]*",
    ...otherPages.map(page => `${page}(/.*)?`),
  ];
  const migrated = new RegExp(`^(${pages.join("|")})?/?$`);
  return ({ urlSettings = "" }) => (migrated.test(urlSettings) ? <SingleChannel /> : <NotFound />);
};

const singleChannelStubs = {
  streaming: singleChannelStub(
    "plan",
    "agent-approvals",
    "status",
    "creatives",
    "reports",
    "optimizations",
    "geo[-_]?response",
    "wizard",
    "buying",
    "diagnostics"
  ),
  audio: singleChannelStub("optimizations", "geo[-_]?response"),
  display: singleChannelStub(),
  linear: singleChannelStub("optimizations", "diagnostics", "orders", "plans", "buying", "spike"),
  radio: singleChannelStub("buying"),
  social: singleChannelStub("meta-creative-health-dashboard", "meta-signal-dashboard"),
  "search-shopping": singleChannelStub("adcopy-ai"),
  programmatic: singleChannelStub(),
  youtube: singleChannelStub("planning"),
  email: singleChannelStub(),
  commerce: singleChannelStub("amc-experiment", "alexa-visibility"),
};

// The legacy YouTube Performance page is Single Channel's Metrics Table now. Keep the company
// prefix and query string so old bookmarks still land on the right data.
const YouTubePerformanceRedirect = ({ uri, location }) => {
  useEffect(() => {
    const companyPrefix = uri.replace(/\/youtube\/performance.*$/, "");
    navigate(`${companyPrefix}/youtube/metrics-table${location.search}`, { replace: true });
  }, [uri, location.search]);
  return null;
};

const CompanyRoutes = ({ company }) => {
  const { location } = useLocation();
  const dispatch = useDispatch();
  const companies = useSelector(UserRedux.companiesSelector);
  const companyInfo = useCompanyInfo();

  const shouldEnableAudioGraphPerformance = !useExperimentFlag("showIPAudioPerformance");
  const shouldEnableCampaignAudit = useExperimentFlag("enableCampaignAudit");
  const shouldEnableCampaignMappingTool = useExperimentFlag("enableCampaignMappingTool");
  const shouldEnableDisplayGraphPerformance = !useExperimentFlag("showIPDisplayPerformance");
  const shouldEnableGraphPerformance = !useExperimentFlag("showIPStreamingPerformance");
  const shouldEnableMobiusSlidesCompany = useExperimentFlag("enableMobiusSlidesCompany");
  const shouldEnableStreamingGeoTargetingExperiment = useExperimentFlag(
    "enableStreamingGeoTargeting"
  );
  const shouldNotDefaultHomeV2 = useExperimentFlag("notDefaultHomeV2");
  const shouldEnableMetaBuyingExperiment = useExperimentFlag("enableMetaBuying");
  const shouldEnableDtcMmm = useExperimentFlag("enableDtcMmm");
  const shouldEnableGeoMMM = useExperimentFlag("enableGeoMMM") || company === "tinuititest";
  const enableHROASFlag = useExperimentFlag("enableHROAS");
  const enableCompanyHROASFlag =
    useExperimentFlag("enableCompanyHROAS") || company === "tinuititest";
  const shouldEnableHROAS = enableHROASFlag && enableCompanyHROASFlag;
  const shouldEnableRadioBuying = useExperimentFlag("enableRadioBuying");
  const shouldEnableForecasting = useExperimentFlag("enableForecasting");
  const shouldEnableBillingAutomation = useExperimentFlag("enableBillingAutomation");
  const shouldEnablePrincipalBasedBuying = useExperimentFlag("enablePrincipalBasedBuying");
  const shouldShowLegacyTVADPages = useExperimentFlag("showLegacyTVADPages");
  const isInternal = useSelector(UserRedux.isInternalSelector);
  const shouldShowCostPages =
    !shouldEnablePrincipalBasedBuying || (shouldEnablePrincipalBasedBuying && isInternal);

  const isValidCompany = useMemo(() => R.any(elem => elem.company === company, companies), [
    companies,
    company,
  ]);

  const pacingClientFacing = R.pathOr(false, ["pacing_client_facing"], companyInfo);

  useEffect(() => {
    // TODO we need to set this to null for non company routes
    Mixpanel.register({ company: company });
  }, [company]);

  useEffect(() => {
    if (company !== company.toLowerCase()) {
      R.pipe(R.prop("pathname"), R.replace(company, company.toLowerCase()), navigate)(location);
      return;
    }
    if (company && isValidCompany && (!companyInfo.cid || company !== companyInfo.cid)) {
      dispatch(SetCompany(company));
    }
  }, [company, isValidCompany, companyInfo, location, dispatch]);

  if (companies && !isValidCompany) {
    return <NotFound invalidCompany={company} />;
  } else if (!companyInfo.cid) {
    return <FullPageSpinner path="*" />;
  } else {
    return (
      <Router className="bpmRouter">
        <ProtectedRoute
          component={migrationStub("Retail Ops Channels")}
          key="retail ops channels"
          path="/retail-ops-channels"
        />
        {/* Home */}
        {shouldNotDefaultHomeV2 ? (
          <>
            <Redirect noThrow from="/" to="home" />
            <HomePage key="home" path="/home" />
          </>
        ) : (
          <>
            <Redirect noThrow from="/" to="cross-channel" />
            <HomePage key="home" path="/home-archived" />
          </>
        )}
        {shouldShowLegacyTVADPages && (
          <TVADCrossChannel key="tvadcrosschannel" path="/tvad-cross-channel" />
        )}
        <SuspenseRoute
          key="crosschannel"
          path="/cross-channel/*urlSettings"
          component={CrossChannel}
        />
        <SuspenseRoute
          key="crosschannel"
          path="/cross_channel/*urlSettings"
          component={CrossChannel}
        />
        <SuspenseRoute
          key="crosschannel"
          path="/crosschannel/*urlSettings"
          component={CrossChannel}
        />
        {/* Metrics */}
        {/* Creatives */}
        <ProtectedRoute
          component={migrationStub("Creative Map")}
          key="creatives"
          path="/creatives/*"
          roleRequired={10}
        />
        <MigrationPage
          key="creative insights"
          path="/creative-insights/*"
          title="Linear Creative Insights"
          pageType="Linear Creative Insights"
        />
        <Redirect noThrow from="/creative_insights" to="../creative-insights" />
        <Redirect noThrow from="/creativeinsights" to="../creative-insights" />
        <MigrationPage
          key="customer insights"
          path="/customer-insights/*"
          title="Customer Insights"
          pageType="Customer Insights"
        />
        <Redirect noThrow from="/customer_insights" to="../customer-insights" />
        <Redirect noThrow from="/customerinsights" to="../customer-insights" />
        <MigrationPage
          key="brand health metrics"
          path="brand-health-metrics/*"
          title="Brand Health Metrics"
          pageType="Brand Health Metrics"
        />
        <Redirect noThrow from="/brand_health_metrics" to="../brand-health-metrics" />
        <ProtectedRoute component={DataFreshness} key="data freshness" path="/data-freshness" />
        <ProtectedRoute
          key="segmentation-mapping"
          path="/segmentation-mapping/*"
          component={migrationStub("Segmentation Mapping")}
          roleRequired={2}
        />
        <ProtectedRoute
          key="sot-ga4-mapping-tool"
          path="/sot-ga4-mapping-tool/*"
          component={migrationStub("SOT/GA4 Mapping Tool")}
        />
        {shouldEnableCampaignAudit && (
          <ProtectedRoute
            key="campaign-audit"
            path="/campaign-audit/*"
            component={migrationStub("Campaign Audit")}
          />
        )}
        {shouldEnableCampaignMappingTool && (
          <ProtectedRoute
            key="campaign-mapping-tool"
            path="/campaign-mapping-tool/*"
            component={CampaignMappingTool}
          />
        )}
        {/* Linear */}
        {shouldShowLegacyTVADPages && (
          <LinearPerformance
            key="linear performance"
            path={"/linear/performance/*urlPresetName"}
            prefix="tv"
          />
        )}
        {shouldShowLegacyTVADPages && (
          <SuspenseRoute
            key="linear delivery"
            path="/linear/delivery"
            component={LinearDelivery}
            prefix="linear"
          />
        )}
        <ProtectedRoute
          key="linear spike"
          path="/linear/spike"
          component={migrationStub("Spike Chart")}
        />
        <ProtectedRoute
          key="linear buying"
          path="/linear/buying"
          component={migrationStub("Linear Buying")}
        />
        <ProtectedRoute
          key="linear plans"
          path="/linear/plans"
          component={migrationStub("Linear Plans")}
        />
        <ProtectedRoute
          key="linear orders"
          path="/linear/orders"
          component={migrationStub("Linear Orders")}
        />
        {/* Radio */}
        {shouldEnableRadioBuying && (
          <ProtectedRoute
            key="radio buying"
            path="/radio/buying"
            component={migrationStub("Radio Buying")}
            channel="radio"
          />
        )}
        {/* Streaming */}
        {shouldShowCostPages && shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="streaming performance"
            path={"/streaming/performance/*urlPresetName"}
            prefix="streaming"
            isGraph={shouldEnableGraphPerformance}
          />
        )}
        {shouldShowCostPages && shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="streaming performance"
            path={"/streaming/performance_graph/*urlPresetName"}
            prefix="streaming"
            isGraph={true}
          />
        )}
        {shouldShowCostPages && shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="streaming performance"
            path={"/streaming/performance_tu/*urlPresetName"}
            prefix="streaming"
            isGraph={true}
            isTransunionGraph={true}
          />
        )}
        {shouldShowCostPages && shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="streaming performance"
            path={"/streaming/performance_ip/*urlPresetName"}
            prefix="streaming"
          />
        )}
        {shouldShowCostPages && shouldShowLegacyTVADPages && (
          <SuspenseRoute
            key="streaming delivery"
            path="/streaming/delivery"
            component={StreamingDelivery}
            prefix="streaming"
            isGraph={shouldEnableGraphPerformance}
          />
        )}
        <ProtectedRoute
          component={singleChannelStubs.streaming}
          key="streaming single channel"
          path={"/streaming/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs.audio}
          key="audio single channel"
          path={"/audio/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs.display}
          key="display single channel"
          path={"/display/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs.linear}
          key="linear single channel"
          path={"/linear/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs.radio}
          key="radio single channel"
          path={"/radio/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs.social}
          key="social single channel"
          path={"/social/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs["search-shopping"]}
          key="search shopping single channel"
          path={"/search-shopping/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs.programmatic}
          key="programmatic single channel"
          path={"/programmatic/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs.youtube}
          key="youtube single channel"
          path={"/youtube/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={singleChannelStubs.email}
          key="email single channel"
          path={"/email/*urlSettings"}
          roleRequired={10}
        />
        {shouldEnableMetaBuyingExperiment && (
          <ProtectedRoute
            key="meta-buying"
            path="/social/meta-buying/*"
            suspense={true}
            component={MetaBuying}
          />
        )}
        <ProtectedRoute key="meta-lift-study" path="/meta-lift-study/*" component={MetaLiftStudy} />
        <ProtectedRoute
          key="meta-signal-dashboard"
          path="/social/meta-signal-dashboard"
          component={migrationStub("Meta Signal Dashboard")}
        />
        <Redirect noThrow from="/meta_signal_dashboard" to="../social/meta-signal-dashboard" />
        <Redirect noThrow from="/metasignaldashboard" to="../social/meta-signal-dashboard" />
        <ProtectedRoute
          component={singleChannelStubs.commerce}
          key="commerce single channel"
          path={"/commerce/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          key="commerce ops"
          path="/commerce-ops/*"
          component={() => <MigrationPage title="Commerce Ops" pageType="Commerce Ops" />}
        />
        {/* Youtube */}
        <ProtectedRoute
          key="youtube planning"
          path="/youtube/planning"
          component={migrationStub("YouTube Planning")}
        />
        <ProtectedRoute
          key="youtube performance"
          path="/youtube/performance/*urlPresetName"
          component={YouTubePerformanceRedirect}
        />
        <MigrationPage
          key="streaming geo response"
          path="/streaming/geo-response"
          title="Streaming Geo Response"
          pageType="Streaming Geo Response"
        />
        <Redirect noThrow from="/streaming/geo_response" to="../streaming/geo-response" />
        <MigrationPage
          key="audio geo response"
          path="/audio/geo-response"
          title="Audio Geo Response"
          pageType="Audio Geo Response"
        />
        <Redirect noThrow from="/audio/geo_response" to="../audio/geo-response" />
        {shouldEnableStreamingGeoTargetingExperiment && (
          <>
            <ProtectedRoute
              key="streaming geo targeting"
              path="/streaming/geo_targeting/*orderInfo"
              component={StreamingGeoTargeting}
              prefix="streaming"
            />
            <ProtectedRoute
              key="streaming geo targeting"
              path="/streaming/geo-targeting/*orderInfo"
              component={StreamingGeoTargeting}
              prefix="streaming"
            />
          </>
        )}
        <ProtectedRoute
          key="streaming creatives"
          path="/streaming/creatives/*"
          component={migrationStub("Creative Allocations")}
        />
        <ProtectedRoute
          suspense
          key="streaming wizard"
          path="/streaming/wizard/*"
          component={StreamingWizard}
        />
        <ProtectedRoute
          key="streaming buying"
          path="/streaming/buying/*"
          component={migrationStub("Streaming Buying")}
        />
        <ProtectedRoute
          key="streaming reports"
          path="/streaming/reports/*"
          component={migrationStub("Streaming Reports")}
        />
        <ProtectedRoute
          key="streaming status"
          path="/streaming/status/*"
          component={migrationStub("Streaming Order Status")}
        />
        {/* Audio */}
        {shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="audio performance"
            path={"/audio/performance/*urlPresetName"}
            prefix="audio"
            isGraph={shouldEnableAudioGraphPerformance}
          />
        )}
        {shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="audio performance"
            path={"/audio/performance_graph/*urlPresetName"}
            prefix="audio"
            isGraph={true}
          />
        )}
        {shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="audio performance"
            path={"/audio/performance_tu/*urlPresetName"}
            prefix="audio"
            isGraph={true}
            isTransunionGraph={true}
          />
        )}
        {shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="audio performance"
            path={"/audio/performance_ip/*urlPresetName"}
            prefix="audio"
          />
        )}
        {shouldShowLegacyTVADPages && (
          <SuspenseRoute
            key="audio delivery"
            path="/audio/delivery"
            component={StreamingDelivery}
            prefix="audio"
            isGraph={shouldEnableAudioGraphPerformance}
          />
        )}
        {/* Display */}
        {shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="display performance"
            path={"/display/performance/*urlPresetName"}
            prefix="display"
            isGraph={shouldEnableDisplayGraphPerformance}
          />
        )}
        {shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="display performance"
            path={"/display/performance_graph/*urlPresetName"}
            prefix="display"
            isGraph={true}
          />
        )}
        {shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="display performance"
            path={"/display/performance_tu/*urlPresetName"}
            prefix="display"
            isGraph={true}
            isTransunionGraph={true}
          />
        )}
        {shouldShowLegacyTVADPages && (
          <StreamingPerformance
            key="display performance"
            path={"/display/performance_ip/*urlPresetName"}
            prefix="display"
          />
        )}
        {shouldShowLegacyTVADPages && (
          <SuspenseRoute
            key="display delivery"
            path="/display/delivery"
            component={StreamingDelivery}
            prefix="display"
            isGraph={shouldEnableDisplayGraphPerformance}
          />
        )}
        <ProtectedRoute key="expectedbookings" path="/bookings" component={ExpectedBookings} />
        {/* Mobius */}
        {shouldEnableMobiusSlidesCompany && (
          <ProtectedRoute
            suspense
            key="mobiusslides"
            path="/mobius-slides"
            component={Slides}
            roleRequired={2}
          />
        )}
        <Redirect noThrow from="/mobius_slides" to="../mobius-slides" />
        <ProtectedRoute
          component={Insights}
          key="amc insights"
          path="/amc-insights/*"
          roleRequired={company === "dripdrop" ? 2 : 10}
        />
        {/* CommerceTool migrated to tinuiti-frontend as Commerce AdOps at the new /adops/commerce
            path. Both stubs keep the routes reachable from the old app (nav + legacy /commerce-tools
            links); MigrationPage reloads so the k8s HTTPRoute serves them from tinuiti-frontend. */}
        <MigrationPage
          key="commerce-ad-ops"
          path="/adops/commerce/*"
          title="Commerce AdOps Tools"
          pageType="Commerce AdOps Tools"
        />
        <MigrationPage
          key="commerce-tools-legacy"
          path="/commerce-tools/*"
          title="Commerce AdOps Tools"
          pageType="Commerce AdOps Tools"
        />
        {/* Competitive */}
        <ProtectedRoute
          key="competitive metrics"
          path="/competitive/metrics"
          component={migrationStub("Competitive Metrics")}
        />
        <MigrationPage
          key="competitive spots"
          path="/competitive/spots"
          title="Competitive Spots"
          pageType="Competitive Spots"
        />
        {/* Channels? */}
        {/* rMMM / Commerce MMM migrated to tinuiti-frontend. These stubs keep the routes
            reachable from the old app (nav + legacy links); MigrationPage reloads so the k8s
            HTTPRoute serves them from tinuiti-frontend. The heavy page source was removed. */}
        <Redirect noThrow from="/mmm/*" to="../rmmm/" />
        <MigrationPage key="rmmm" path="/rmmm/*" title="rMMM" pageType="rMMM" />
        <MigrationPage
          key="commerce-mmm"
          path="/commerce-mmm/*"
          title="Commerce MMM"
          pageType="Commerce MMM"
        />
        {shouldEnableHROAS && (
          <ProtectedRoute key="hroas" path="/hroas/*" component={HROAS} roleRequired={1} />
        )}
        {shouldEnableGeoMMM && (
          <ProtectedRoute
            key="geo mmm"
            path="/geo-mmm/*"
            component={migrationStub("Geo MMM")}
            roleRequired={10}
          />
        )}
        {shouldEnableDtcMmm && (
          <ProtectedRoute
            key="dtc-mmm"
            path="/mmm-model-results/dtc-mmm"
            component={migrationStub("DTC MMM")}
          />
        )}
        <ProtectedRoute
          key="feature-mapping"
          path="mmm/feature-mapping"
          component={migrationStub("MMM Feature Mapping")}
        />
        <ProtectedRoute
          key="custom tableau"
          path="/custom/*dashboardName"
          component={CustomTableau}
          roleRequired={10}
        />
        {/* Admin */}
        <ProtectedRoute
          key="kpiMapping"
          path="/kpi-mapping"
          component={migrationStub("KPI Mapping")}
          roleRequired={3}
        />
        <ProtectedRoute
          key="fiscalCalendarInput"
          path="/calendar-input"
          component={migrationStub("Calendar Inputs")}
        />
        <Redirect noThrow from="/powerpoint" to="../slides" />
        <ProtectedRoute suspense key="slides" path="/slides" component={Slides} />
        <ProtectedRoute
          suspense
          key="slide-generator"
          path="/slide-generator"
          component={migrationStub("Slide Generator")}
        />
        {shouldEnableBillingAutomation && (
          <ProtectedRoute
            key="billing-automation"
            path="/billing-automation"
            component={migrationStub("Billing Automation")}
          />
        )}
        <ProtectedRoute
          key="linear_optimizations"
          path="/linear/optimizations/*"
          component={migrationStub("Linear Optimizations")}
        />
        <ProtectedRoute
          key="streaming_optimizations"
          path="/streaming/optimizations/*"
          component={migrationStub("Streaming Optimizations")}
        />
        <ProtectedRoute
          key="audio_optimizations"
          path="/audio/optimizations/*"
          component={migrationStub("Audio Optimizations")}
        />
        <MigrationPage key="reporting" path="/reporting/*" title="Reporting" pageType="Reporting" />
        {/* Properties */}
        <ProtectedRoute key="properties" path="/properties" component={Properties} />
        <ProtectedRoute
          key="linear_diagnostics"
          path="/linear/diagnostics/*"
          component={migrationStub("Linear ML Diagnostics")}
        />
        <ProtectedRoute
          key="streaming_diagnostics"
          path="/streaming/diagnostics/*"
          component={migrationStub("Streaming ML Diagnostics")}
        />
        <MigrationPage
          key="goalintaketool"
          path="goal-intake-tool/*"
          title="Goal Intake Tool"
          pageType="Goal Intake Tool"
        />
        <Redirect noThrow from="/budget-intake-tool/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/budgetintaketool/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/budget_intake_tool/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/budget-intake/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/budgetintake/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/budget_intake/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/goalintaketool/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/goal_intake_tool/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/goal-intake/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/goalintake/*" to="../goal-intake-tool" />
        <Redirect noThrow from="/goal_intake/*" to="../goal-intake-tool" />
        <ProtectedRoute
          key="pacing"
          path="pacing/*"
          component={migrationStub("Cross-Channel Pacing")}
          roleRequired={pacingClientFacing ? 10 : 1}
        />
        <ProtectedRoute
          key="cro-onboard"
          path="/cro-onboard"
          component={migrationStub("CRO Onboard")}
          roleRequired={2}
        />
        <Redirect noThrow from="/croonboard" to="../cro-onboard" />
        <ProtectedRoute key="wto" path="/wto" component={WebTrafficOptimization} />
        <ProtectedRoute
          key="client tools"
          path="/edit-client"
          component={migrationStub("Edit Client")}
          roleRequired={1}
        />
        <Redirect noThrow from="/edit_client" to="../edit-client" />
        <ProtectedRoute
          key="brandequity"
          path="brand-equity/*"
          component={migrationStub("Brand Equity")}
          roleRequired={10}
        />
        <Redirect noThrow from="/brand_equity" to="../brand-equity" />
        <Redirect noThrow from="/brandequity" to="../brand-equity" />
        <ProtectedRoute
          key="brandequityamazon"
          path="brand-equity-amazon/*"
          component={migrationStub("Brand Equity")}
          roleRequired={2}
        />
        {shouldEnableForecasting && (
          <ProtectedRoute
            key="forecasting"
            path="/forecasting/*"
            component={migrationStub("Forecasting")}
          />
        )}
        <ProtectedRoute
          key="generativeadcopy"
          path="adcopy-ai/*"
          component={migrationStub("AdCopy AI")}
        />
        <ProtectedRoute
          key="xclientbenchamarking"
          path="cross-client-benchmarking/*"
          component={migrationStub("Benchmarking")}
        />
        <NotFound key="404" default />
      </Router>
    );
  }
};

export default CompanyRoutes;
